import { Alert, Button } from "@opowiedzo/ui";
import { useCustomMutation, useGetIdentity, useInvalidate, useOne } from "@refinedev/core";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import { Labeled, TextArea } from "@/components/Form";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import { familyRoleLabel, formatDateTime } from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";
import type { UserRow } from "./list";

interface UserFamily {
  family_id: string;
  family_name: string;
  role: string;
  is_owner: boolean;
  memoirs_count: number;
}

interface UserDetail extends UserRow {
  families: UserFamily[];
}

export function UserShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<UserDetail>({ resource: "users", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const user = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!user) return null;

  const permissions = identity?.permissions ?? [];

  return (
    <>
      <BackLink to="/uzytkownicy">Konta rodzin</BackLink>
      <PageHeader
        title={user.name}
        description={user.email}
        actions={
          <Badge tone={user.is_active ? "ok" : "error"}>
            {user.is_active ? "Konto czynne" : "Konto zablokowane"}
          </Badge>
        }
      />

      <DetailGrid
        main={
          <>
            <Card title="Konto">
              <DefinitionList
                rows={[
                  ["E-mail", user.email],
                  ["Adres potwierdzony", formatDateTime(user.email_verified_at)],
                  ["Ostatnie logowanie", formatDateTime(user.last_login_at)],
                  ["Konto założone", formatDateTime(user.created_at)],
                ]}
              />
            </Card>

            <Card title="Rodziny">
              {user.families.length === 0 ? (
                <p className="text-base text-ink-soft">To konto nie należy do żadnej rodziny.</p>
              ) : (
                <ul className="flex flex-col gap-2.5 text-base">
                  {user.families.map((family) => (
                    <li key={family.family_id}>
                      <Link
                        to={`/opowiesci?family_id=${family.family_id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {family.family_name}
                      </Link>
                      <span className="block text-xs text-ink-soft">
                        {familyRoleLabel(family.role)}
                        {family.is_owner && " - właściciel rodziny"} - {family.memoirs_count}{" "}
                        Opowieści
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        }
        side={
          <>
            <AccountPanel user={user} allowed={permissions.includes("users.write")} />
            <ImpersonationPanel
              user={user}
              allowed={permissions.includes("users.impersonate")}
              active={identity?.impersonating?.user_id === user.id}
            />
          </>
        }
      />
    </>
  );
}

function AccountPanel({ user, allowed }: { user: UserDetail; allowed: boolean }) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  const [powod, setPowod] = useState("");

  if (!allowed) {
    return (
      <Card title="Blokada konta">
        <p className="text-base text-ink-soft">
          Blokadę konta rodziny zdejmuje i nakłada wyłącznie superadmin.
        </p>
      </Card>
    );
  }

  const akcja = (sciezka: string) =>
    mutate(
      {
        url: `/users/${user.id}/${sciezka}`,
        method: "post",
        values: { reason: powod.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setPowod("");
          invalidate({ resource: "users", invalidates: ["detail", "list"], id: user.id });
        },
      },
    );

  return (
    <Card title="Blokada konta">
      <div className="flex flex-col gap-3">
        <Labeled label="Powód (trafi do audytu)">
          <TextArea rows={2} value={powod} onChange={(event) => setPowod(event.target.value)} />
        </Labeled>
        {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}
        <Button
          className="self-start"
          disabled={powod.trim().length < 3 || mutation.isPending}
          onClick={() => akcja(user.is_active ? "deactivate" : "activate")}
        >
          {user.is_active ? "Zablokuj konto" : "Odblokuj konto"}
        </Button>
        <CardNote>
          Blokada zamyka wszystkie sesje tej osoby od razu. Historie i Opowieści zostają nietknięte
          - usunięcie danych na żądanie idzie osobną drogą.
        </CardNote>
      </div>
    </Card>
  );
}

function ImpersonationPanel({
  user,
  allowed,
  active,
}: {
  user: UserDetail;
  allowed: boolean;
  active: boolean;
}) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  const [powod, setPowod] = useState("");

  if (!allowed) return null;

  return (
    <Card title="Wejście na konto rodziny">
      {active ? (
        <p className="text-base text-ink-soft">
          Masz otwartą sesję tej rodziny. Wyjście jest w pasku na górze każdego ekranu.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <Labeled label="Powód (obowiązkowy, trafi do audytu)">
            <TextArea
              rows={2}
              value={powod}
              placeholder="np. zgłoszenie #124 - rodzina nie widzi zdjęć w albumie"
              onChange={(event) => setPowod(event.target.value)}
            />
          </Labeled>
          {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}
          <Button
            className="self-start"
            disabled={powod.trim().length < 3 || mutation.isPending || !user.is_active}
            onClick={() =>
              mutate(
                {
                  url: `/users/${user.id}/impersonate`,
                  method: "post",
                  values: { reason: powod.trim() },
                  successNotification: false,
                  errorNotification: false,
                },
                {
                  onSuccess: () => {
                    setPowod("");
                    // Pasek z wyjściem bierze stan z tożsamości admina.
                    invalidate({ resource: "users", invalidates: ["detail"], id: user.id });
                    window.location.reload();
                  },
                },
              )
            }
          >
            Wejdź na konto tej rodziny
          </Button>
          <CardNote>
            Sesja gaśnie po 30 minutach, a wejście i wyjście zostają w audycie razem z powodem.
            Widzisz wtedy dokładnie to, co widzi rodzina - łącznie z treścią jej historii.
          </CardNote>
        </div>
      )}
    </Card>
  );
}
